import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { 
  UploadResponse, 
  EvaluationSummary, 
  Histogram, 
  MetadataFilters,
  DefectTypeDistribution as DefectTypeDistributionType, 
  WelderPerformance, 
  TimeSeriesDataPoint, 
  RCAAnalysis as RCAAnalysisType, 
  LocationAnalysis, 
  ShiftComparison as ShiftComparisonType,
  WelderDetailedAnalysis
} from '../types/stats';
import { MetricCard } from './MetricCard';
import { formatCount } from '../utils/format';
import { EvaluationTabs } from './EvaluationTabs';
import { StatTable } from './StatTable';
import { HistogramBarChart, EvaluationSelection } from './charts/HistogramBarChart';
import { StartDistanceCharts } from './charts/StartDistanceCharts';
import { FilterPanel } from './FilterPanel';
import { DefectTypeDistribution } from './charts/DefectTypeDistribution';
import { WelderPerformanceDashboard } from './charts/WelderPerformanceDashboard';
import { TimeSeriesTrends } from './charts/TimeSeriesTrends';
import { RCAAnalysis } from './charts/RCAAnalysis';
import { LocationPositionalAnalysis } from './charts/LocationPositionalAnalysis';
import { ShiftComparison } from './charts/ShiftComparison';
import { WelderDefectHeatmap } from './charts/WelderDefectHeatmap';
import { PositionDefectRadar } from './charts/PositionDefectRadar';
import { IndividualWelderAnalysis } from './charts/IndividualWelderAnalysis';
import { InsightsPanel } from './InsightsPanel';
import { MLInsightsPanel } from './MLInsightsPanel';
import { fetchAIInsights } from '../../../redux/NDT Analytics/actions';
import type { NdtCategoryData, CategoryFieldConfig, PipeOption } from './FilterPanel';

interface ProjectDetailViewProps {
  data: UploadResponse | null;
  selectedProject: string;
  filters: MetadataFilters | null;
  selectedFilters: {
    project?: string | null;
    subProject?: string[] | string | null;
    pipeId?: string[] | string | null;
    pipeDiameter?: string | null;
    wallThickness?: string[] | string | null;
    shift?: string | null;
    location?: string[] | string | null;
    criticalWeld?: string | null;
    wpsNumber?: string[] | string | null;
  };
  onFilterChange: (filters: {
    project?: string | null;
    subProject?: string[] | string | null;
    pipeId?: string[] | string | null;
    pipeDiameter?: string | null;
    wallThickness?: string[] | string | null;
    shift?: string | null;
    location?: string[] | string | null;
    criticalWeld?: string | null;
    wpsNumber?: string[] | string | null;
  }) => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string | null;
  isNoRecordsFound?: boolean;
  currentFile?: File | null;
  onDataUpdate?: (data: UploadResponse) => void;
  projectId?: string | number | null;
  categoryData?: NdtCategoryData | null;
  categoryFields?: CategoryFieldConfig[];
  pipeOptions?: PipeOption[];
}

const MeasurementTabs = ({ evaluation }: { evaluation: EvaluationSummary }) => {
  const [activeMeasurement, setActiveMeasurement] = useState<string>(
    evaluation.variables[0]?.variable ?? '',
  );

  const formatMeasurementLabel = (label: string) => label.replace(/\s*\(mm\)$/i, '').trim();

  if (!evaluation.variables.length) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No measurements available for this evaluation.
      </p>
    );
  }

  const current = evaluation.variables.find((variable) => variable.variable === activeMeasurement);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {evaluation.variables.map((variable) => (
          <button
            key={variable.variable}
            type="button"
            onClick={() => setActiveMeasurement(variable.variable)}
            className={clsx(
              'rounded-full border px-3 py-1 text-sm font-medium transition',
              activeMeasurement === variable.variable
                ? 'border-hero-red-20 bg-hero-red-10 text-readable-red'
                : 'border-[#E2E5EA] bg-white text-blueprint-blue hover:border-[#C0C5CE]',
            )}
          >
            {formatMeasurementLabel(variable.variable)}
          </button>
        ))}
      </div>
      {current ? <StatTable summary={current} /> : null}
    </div>
  );
};

const VISUAL_MEASUREMENT_ORDER = ['Length (mm)', 'Depth (mm)', 'Height (mm)', 'Start Distance'];

const formatMeasurementLabel = (label: string) => label.replace(/\s*\(mm\)\s*/gi, '').trim();

type DashboardType = 'measurements' | 'defect-types' | 'welders' | 'time-trends' | 'location' | 'rca';

// Measurement Dashboard Component
const MeasurementDashboard = ({
  histograms
}: {
  histograms: Record<string, Histogram>;
}) => {
  const histogramOptions = useMemo(() => {
    const ordered = VISUAL_MEASUREMENT_ORDER.filter((key) => histograms[key]);
    const remaining = Object.keys(histograms).filter((key) => !ordered.includes(key));
    return [...ordered, ...remaining].map((key) => ({
      key,
      label: formatMeasurementLabel(key) || key,
    }));
  }, [histograms]);

  const [selectedMeasurement, setSelectedMeasurement] = useState<string>(histogramOptions[0]?.key ?? '');
  const [evaluationSelection, setEvaluationSelection] = useState<EvaluationSelection>({
    Accepted: true,
    Rejected: true,
  });

  const histogram = histograms[selectedMeasurement];
  const isStartDistance = selectedMeasurement.toLowerCase().includes('start');
  const displayHistogram = useMemo(() => {
    if (!histogram) return histogram;
    if (!isStartDistance) return histogram;
    return {
      ...histogram,
      bins: histogram.bins.map((bin) => ({
        ...bin,
        label: formatMeasurementLabel(bin.value.toString()),
      })),
    };
  }, [histogram, isStartDistance]);

  if (histogramOptions.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No measurement data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        {histogramOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setSelectedMeasurement(option.key)}
            className={clsx(
              'rounded-full border px-3 py-1 text-sm font-medium transition',
              option.key === selectedMeasurement
                ? 'border-hero-red-20 bg-hero-red-10 text-readable-red'
                : 'border-[#E2E5EA] bg-white text-blueprint-blue hover:border-[#C0C5CE]',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        {(Object.keys(evaluationSelection) as Array<keyof EvaluationSelection>).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() =>
              setEvaluationSelection((prev) => {
                const enabledCount = Object.values(prev).filter(Boolean).length;
                const nextValue = !prev[key];
                if (!nextValue && enabledCount === 1) {
                  return prev;
                }
                return { ...prev, [key]: nextValue };
              })
            }
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-widest transition',
              evaluationSelection[key]
                ? 'border-hero-red-20 bg-hero-red-10 text-readable-red'
                : 'border-[#E2E5EA] bg-white text-blueprint-blue hover:border-[#C0C5CE]',
            )}
          >
            {key}
          </button>
        ))}
      </div>

      {isStartDistance ? (
        displayHistogram && (
          <StartDistanceCharts histogram={displayHistogram} evaluations={evaluationSelection} />
        )
      ) : (
        histogram && (
          <HistogramBarChart
            histogram={histogram}
            evaluations={evaluationSelection}
            title={`${formatMeasurementLabel(selectedMeasurement)} distribution`}
          />
        )
      )}
    </div>
  );
};

// Defect Type Dashboard Component
const DefectTypeDashboard = ({
  defectTypeDistribution
}: {
  defectTypeDistribution?: DefectTypeDistributionType[];
}) => {
  if (!defectTypeDistribution || defectTypeDistribution.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No defect type data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <DefectTypeDistribution data={defectTypeDistribution} />
    </div>
  );
};

// Welder Dashboard Component
const WelderDashboard = ({
  welderPerformance,
  welderDetailedAnalysis
}: {
  welderPerformance?: WelderPerformance[];
  welderDetailedAnalysis?: WelderDetailedAnalysis;
}) => {
  const [activeView, setActiveView] = useState<'performance' | 'heatmap-defects' | 'heatmap-positions' | 'position-radar' | 'individual'>('performance');

  const hasPerformance = welderPerformance && welderPerformance.length > 0;
  const hasDetailed = welderDetailedAnalysis &&
    Object.keys(welderDetailedAnalysis.welderDefectMatrix || {}).length > 0;

  if (!hasPerformance && !hasDetailed) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No welder performance data available.
      </p>
    );
  }

  const views = [
    { id: 'performance', label: 'Performance', available: hasPerformance },
    { id: 'heatmap-defects', label: 'Welder vs Defects', available: hasDetailed },
    { id: 'heatmap-positions', label: 'Welder vs Positions', available: hasDetailed },
    { id: 'position-radar', label: 'Position vs Defects', available: hasDetailed && welderDetailedAnalysis?.allPositions && welderDetailedAnalysis.allPositions.length > 0 },
    { id: 'individual', label: 'Individual Analysis', available: hasDetailed },
  ].filter(v => v.available);

  return (
    <div className="space-y-6">
      {/* View Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-4">
        {views.map((view) => (
          <button
            key={view.id}
            onClick={() => setActiveView(view.id as typeof activeView)}
            className={clsx(
              'rounded-full px-4 py-1.5 text-sm font-medium transition border border-[#E2E5EA]',
              activeView === view.id
                ? 'bg-hero-red-10 text-readable-red border-hero-red-20'
                : 'text-blueprint-blue bg-white border-[#E2E5EA] hover:border-[#C0C5CE] hover:bg-[#FAFBFC]',
            )}
          >
            {view.label}
          </button>
        ))}
      </div>

      {/* View Content */}
      <div className="mt-6">
        {activeView === 'performance' && hasPerformance && (
          <WelderPerformanceDashboard data={welderPerformance} />
        )}
        {activeView === 'heatmap-defects' && hasDetailed && (
          <WelderDefectHeatmap data={welderDetailedAnalysis} showPositions={false} />
        )}
        {activeView === 'heatmap-positions' && hasDetailed && (
          <WelderDefectHeatmap data={welderDetailedAnalysis} showPositions={true} />
        )}
        {activeView === 'position-radar' && hasDetailed && (
          <PositionDefectRadar data={welderDetailedAnalysis} />
        )}
        {activeView === 'individual' && hasDetailed && (
          <IndividualWelderAnalysis data={welderDetailedAnalysis} />
        )}
      </div>
    </div>
  );
};

// Time & Trends Dashboard Component
const TimeTrendsDashboard = ({
  timeSeriesTrends,
  shiftComparison
}: {
  timeSeriesTrends?: TimeSeriesDataPoint[];
  shiftComparison?: ShiftComparisonType[];
}) => {
  const hasData = (timeSeriesTrends && timeSeriesTrends.length > 0) ||
    (shiftComparison && shiftComparison.length > 0);

  if (!hasData) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No time series or trend data available.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {timeSeriesTrends && timeSeriesTrends.length > 0 && (
        <TimeSeriesTrends data={timeSeriesTrends} />
      )}
      {shiftComparison && shiftComparison.length > 0 && (
        <ShiftComparison data={shiftComparison} />
      )}
    </div>
  );
};

// Location Dashboard Component
const LocationDashboard = ({
  locationAnalysis
}: {
  locationAnalysis?: LocationAnalysis[];
}) => {
  if (!locationAnalysis || locationAnalysis.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No location analysis data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <LocationPositionalAnalysis data={locationAnalysis} />
    </div>
  );
};

// RCA Dashboard Component
const RCADashboard = ({
  rcaAnalysis
}: {
  rcaAnalysis?: RCAAnalysisType[];
}) => {
  if (!rcaAnalysis || rcaAnalysis.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No root cause analysis data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <RCAAnalysis data={rcaAnalysis} />
    </div>
  );
};

// Main Visualization Panel with Dashboard Navigation
const VisualizationPanel = ({
  histograms,
  insights
}: {
  histograms: Record<string, Histogram>;
  insights?: UploadResponse['insights'];
}) => {
  const [activeDashboard, setActiveDashboard] = useState<DashboardType>('measurements');

  const hasHistograms = Object.keys(histograms).length > 0;
  const hasDefectTypes = insights?.defectTypeDistribution && insights.defectTypeDistribution.length > 0;
  const hasWelders = insights?.welderPerformance && insights.welderPerformance.length > 0;
  const hasTimeTrends = (insights?.timeSeriesTrends && insights.timeSeriesTrends.length > 0) ||
    (insights?.shiftComparison && insights.shiftComparison.length > 0);
  const hasLocation = insights?.locationAnalysis && insights.locationAnalysis.length > 0;
  const hasRCA = insights?.rcaAnalysis && insights.rcaAnalysis.length > 0;

  // Determine available dashboards
  const availableDashboards = useMemo(() => {
    const dashboards: Array<{ id: DashboardType; label: string; available: boolean }> = [
      { id: 'measurements', label: 'Measurements', available: hasHistograms ?? false },
      { id: 'defect-types', label: 'Defect Types', available: hasDefectTypes ?? false },
      { id: 'welders', label: 'Welders', available: hasWelders ?? false },
      { id: 'time-trends', label: 'Time & Trends', available: hasTimeTrends ?? false },
      { id: 'location', label: 'Location', available: hasLocation ?? false },
      { id: 'rca', label: 'Root Cause', available: hasRCA ?? false },
    ];
    return dashboards.filter(d => d.available);
  }, [hasHistograms, hasDefectTypes, hasWelders, hasTimeTrends, hasLocation, hasRCA]);

  // Auto-select first available dashboard if current is not available
  useEffect(() => {
    if (availableDashboards.length > 0 && !availableDashboards.find(d => d.id === activeDashboard)) {
      setActiveDashboard(availableDashboards[0].id);
    }
  }, [availableDashboards, activeDashboard]);

  if (availableDashboards.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No visualization data available.
      </p>
    );
  }

  const renderDashboardContent = () => {
    switch (activeDashboard) {
      case 'measurements':
        return <MeasurementDashboard histograms={histograms} />;
      case 'defect-types':
        return <DefectTypeDashboard defectTypeDistribution={insights?.defectTypeDistribution} />;
      case 'welders':
        return (
          <WelderDashboard
            welderPerformance={insights?.welderPerformance}
            welderDetailedAnalysis={insights?.welderDetailedAnalysis}
          />
        );
      case 'time-trends':
        return (
          <TimeTrendsDashboard
            timeSeriesTrends={insights?.timeSeriesTrends}
            shiftComparison={insights?.shiftComparison}
          />
        );
      case 'location':
        return <LocationDashboard locationAnalysis={insights?.locationAnalysis} />;
      case 'rca':
        return <RCADashboard rcaAnalysis={insights?.rcaAnalysis} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-4">
        {availableDashboards.map((dashboard) => (
          <button
            key={dashboard.id}
            onClick={() => setActiveDashboard(dashboard.id)}
            className={clsx(
              'rounded-full px-4 py-1.5 text-sm font-medium transition border border-[#E2E5EA]',
              activeDashboard === dashboard.id
                ? 'bg-hero-red-10 text-readable-red border-hero-red-20'
                : 'text-blueprint-blue bg-white border-[#E2E5EA] hover:border-[#C0C5CE] hover:bg-[#FAFBFC]',
            )}
          >
            {dashboard.label}
          </button>
        ))}
      </div>

      {/* Dashboard Content */}
      <div className="mt-6">
        {renderDashboardContent()}
      </div>
    </div>
  );
};

export const ProjectDetailView = ({
  data,
  selectedProject,
  filters,
  selectedFilters,
  onFilterChange,
  onBack,
  isLoading = false,
  error = null,
  isNoRecordsFound = false,
  currentFile,
  onDataUpdate,
  projectId,
  categoryData = null,
  categoryFields = [],
  pipeOptions = [],
}: ProjectDetailViewProps) => {
  const dispatch = useDispatch();
  const { aiInsightsData, isFetchingAIInsights, aiInsightsError } = useSelector(
    (state: any) => state.ndtAnalytics || {}
  );

  const [selectedEvaluation, setSelectedEvaluation] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'statistics' | 'visualizations' | 'welder-performance' | 'insights' | 'ml-insights'>('statistics');

  // Fetch AI Insights when ml-insights tab is active
  useEffect(() => {
    if (activeTab === 'ml-insights' && !aiInsightsData && !isFetchingAIInsights) {
      const toArr = (v: string[] | string | null | undefined) => {
        if (v == null) return null;
        if (Array.isArray(v)) return v.length > 0 ? v : null;
        return v !== '' ? [String(v)] : null;
      };
      const toPipeIdApi = (pipeId: string[] | string | null | undefined): number | number[] | null => {
        if (pipeId == null) return null;
        const ids = (Array.isArray(pipeId) ? pipeId : [pipeId])
          .filter((id) => id != null && id !== '')
          .map(Number)
          .filter((id) => !Number.isNaN(id));
        if (ids.length === 0) return null;
        if (ids.length === 1) return ids[0];
        return ids;
      };
      const payload = {
        project: projectId || null,
        pipe_id: toPipeIdApi(selectedFilters?.pipeId),
        sub_project: toArr(selectedFilters?.subProject),
        pipe_diameter: selectedFilters?.pipeDiameter || null,
        wall_thickness: toArr(selectedFilters?.wallThickness),
        shift: selectedFilters?.shift || null,
        location: toArr(selectedFilters?.location),
        critical_weld: selectedFilters?.criticalWeld || null,
        wps_number: toArr(selectedFilters?.wpsNumber),
        welder_number: null,
        welder_repair_number: null,
        evaluation: null,
        defect_type: null,
        start_date: null,
        end_date: null,
        limit: null
      };
      console.log(' Ml api payload', payload);
      dispatch(fetchAIInsights(payload));
    }
  }, [activeTab, aiInsightsData, isFetchingAIInsights, projectId, selectedFilters, dispatch]);

  const currentEvaluation = useMemo(
    () => data?.evaluations?.find((evaluation) => evaluation.evaluation === selectedEvaluation) ?? null,
    [data, selectedEvaluation],
  );

  const metrics = useMemo(() => {
    const placeholder = [
      { label: 'Total welds', value: '—', sublabel: 'Awaiting upload' },
      { label: 'Defect types', value: '—', sublabel: 'Distinct comments' },
      { label: 'Accepted vs rejected', value: '—', sublabel: 'No data yet' },
      { label: 'Total records', value: '—', sublabel: 'Upload to calculate' },
    ];
    if (!data?.metadata || !Array.isArray(data?.evaluations)) {
      return placeholder;
    }

    const totalRows = formatCount(data.metadata.totalRows);
    const totalWelds = formatCount(data.metadata.uniqueWelds ?? data.metadata.totalRows);
    const commentCount = formatCount(data.metadata.commentCount ?? data.metadata.totalRows);
    const accepted = data.evaluations.find((e) => e.evaluation === 'Accepted')?.rowCount ?? 0;
    const rejected = data.evaluations.find((e) => e.evaluation === 'Rejected')?.rowCount ?? 0;

    return [
      {
        label: 'Total welds',
        value: totalWelds,
        sublabel: `${totalRows} rows processed`,
      },
      {
        label: 'Defect types',
        value: commentCount,
        sublabel: 'Unique defect types in dataset',
      },
      {
        label: 'Accepted vs rejected',
        value: `${formatCount(accepted)} / ${formatCount(rejected)}`,
        sublabel: 'Rows marked Accepted vs Rejected',
      },
      {
        label: 'Total records',
        value: totalRows,
        sublabel: `${data.metadata.measurements?.length ?? 0} measurement fields`,
      },
    ];
  }, [data]);

  const evaluationStats = useMemo(() => {
    if (!currentEvaluation) {
      return null;
    }
    const referenceVariable = currentEvaluation.variables[0];
    if (!referenceVariable) {
      return {
        uniqueDefects: 0,
        totalRecords: currentEvaluation.rowCount,
        indications: 0,
        noIndications: currentEvaluation.rowCount,
      };
    }
    const rows = referenceVariable.rows;
    const uniqueDefects = rows.filter((row) => row.comment !== 'No Indication').length;
    const indications = rows.reduce((sum, row) => {
      if (row.comment === 'No Indication') {
        return sum;
      }
      return sum + row.totalCount;
    }, 0);
    const totalRecords = currentEvaluation.rowCount;
    const noIndications = totalRecords - indications;
    return { uniqueDefects, totalRecords, indications, noIndications };
  }, [currentEvaluation]);

  const renderTabContent = () => {
    if (activeTab === 'visualizations') {
      if (!data) {
        return (
          <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
            No data available.
          </p>
        );
      }
      const histograms = data.histograms ?? {};
      const insights = data.insights;
      return <VisualizationPanel histograms={histograms} insights={insights} />;
    }

    if (activeTab === 'welder-performance') {
      if (!data) {
        return (
          <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
            No data available.
          </p>
        );
      }
      const insights = data.insights;
      return (
        <WelderDashboard
          welderPerformance={insights?.welderPerformance}
          welderDetailedAnalysis={insights?.welderDetailedAnalysis}
        />
      );
    }

    if (activeTab === 'insights') {
      return <InsightsPanel insights={data?.aiInsights || null} isLoading={isLoading} />;
    }

    if (activeTab === 'ml-insights') {
      // Use Redux AI Insights data directly (API returns ML insights data structure)
      // The response structure is: { summary, patterns, correlations, anomalies, ... }
      const mlInsights = aiInsightsData?.mlInsights || null;
      // console.log('ML Insights data being passed to MLInsightsPanel:', mlInsights);
      // console.log('aiInsightsData from Redux:', aiInsightsData);
      // console.log('isFetchingAIInsights:', isFetchingAIInsights);
      // console.log('ML Insights Panel - Current data:', {
      //   hasMlInsights: aiInsightsData?.mlInsights,
      //   patterns: aiInsightsData?.mlInsights?.patterns?.length || 0,
      //   correlations: aiInsightsData?.mlInsights?.correlations?.length || 0,
      //   anomalies: aiInsightsData?.mlInsights?.anomalies?.length || 0,
      // });
      return   <MLInsightsPanel insights={mlInsights} isLoading={isFetchingAIInsights || isLoading} /> ;
    }

    if (!data) {
      return (
        <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
          No data available.
        </p>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Evaluations</p>
            <h2 className="text-lg font-semibold text-slate-900">Explore acceptance vs rejection trends</h2>
            {evaluationStats && (
              <p className="mt-2 text-xs text-slate-500">
                Unique defects:&nbsp;
                <span className="font-semibold text-slate-700">{evaluationStats.uniqueDefects}</span>
                &nbsp;• Total records:&nbsp;
                <span className="font-semibold text-slate-700">{formatCount(evaluationStats.totalRecords)}</span>
                &nbsp;• Indications:&nbsp;
                <span className="font-semibold text-slate-900">{formatCount(evaluationStats.indications)}</span>
                <span className="text-slate-500"> / {formatCount(evaluationStats.totalRecords)}</span>
                &nbsp;• No indications:&nbsp;
                <span className="font-semibold text-slate-900">{formatCount(evaluationStats.noIndications)}</span>
                <span className="text-slate-500"> / {formatCount(evaluationStats.totalRecords)}</span>
              </p>
            )}
          </div>
          <EvaluationTabs
            evaluations={data.evaluations}
            selected={selectedEvaluation}
            onSelect={(evaluationName) => {
              setSelectedEvaluation(evaluationName);
              const evaluationNames = data.evaluations.map((e) => e.evaluation);
              if (!evaluationName && evaluationNames.length > 0) {
                setSelectedEvaluation(evaluationNames[0]);
              }
            }}
          />
        </div>
        {currentEvaluation ? (
          <MeasurementTabs evaluation={currentEvaluation} />
        ) : (
          <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
            Select an evaluation above to display its statistics.
          </p>
        )}
      </>
    );
  };

  // Initialize selected evaluation - auto-select if only one exists or current selection is invalid
  useEffect(() => {
    const evaluations = data?.evaluations;
    if (!evaluations?.length) return;
    const evaluationNames = evaluations.map((evaluation) => evaluation.evaluation);

    // Always auto-select if there's only one evaluation
    if (evaluationNames.length === 1) {
      setSelectedEvaluation(evaluationNames[0]);
    }
    // If no evaluation is selected, or the selected one doesn't exist in current data
    else if (!selectedEvaluation || !evaluationNames.includes(selectedEvaluation)) {
      // If multiple evaluations, prefer the one with the most data
      const evaluationWithMostData = evaluations.reduce((prev, current) =>
        current.rowCount > prev.rowCount ? current : prev
      );
      setSelectedEvaluation(evaluationWithMostData.evaluation);
    }
  }, [data, selectedEvaluation]);

  const contentArea = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-12 whitespace-nowrap gap-2 items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                <span className="text-gray-600 text-lg">Loading NDT Analytics data...</span>
        </div>
      );
    }
    if (isNoRecordsFound) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-2">No records found for this project.</p>
            <p className="text-gray-500 text-sm">Please add NDT records for this project.</p>
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-500 text-lg">Error loading data: {error}</p>
          </div>
        </div>
      );
    }
    const hasValidData = data && data.metadata && Array.isArray(data.evaluations);
    if (!hasValidData) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-gray-600 text-lg">No data available</p>
          </div>
        </div>
      );
    }
    return (
      <>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => (
            <MetricCard key={metric.label} {...metric} />
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-slate-200 pb-4">
            {[
              { id: 'statistics', label: 'Statistics' },
              { id: 'visualizations', label: 'Visualizations' },
              { id: 'welder-performance', label: 'Welder Performance' },
              { id: 'insights', label: 'Insights' },
              { id: 'ml-insights', label: 'AI Insights' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={clsx(
                  'rounded-full px-4 py-1.5 text-sm font-medium transition border border-[#E2E5EA]',
                  activeTab === tab.id
                    ? 'bg-hero-red-10 text-readable-red border-hero-red-20'
                    : 'text-blueprint-blue bg-white border-[#E2E5EA] hover:border-[#C0C5CE] hover:bg-[#FAFBFC]',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="space-y-6">{renderTabContent()}</div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-5">
      <FilterPanel
        filters={filters}
        selection={selectedFilters}
        onChange={onFilterChange}
        hideProjectFilter={true}
        preserveProjectOnClear={true}
        categoryData={categoryData}
        categoryFields={categoryFields}
        pipeOptions={pipeOptions}
      />

      {contentArea()}
    </div>
  );
};

